import type { Coords, LocationSuggestion } from '@ixigo/cab-core';
import fetch from '@ixigo/fetch';

/** Location identifier - either coordinates or placeId */
export type LocationIdentifier = { coordinates: Coords } | { placeId: string };

const toRequestItem = (identifier: LocationIdentifier) =>
	'coordinates' in identifier
		? {
				coordinates: {
					latitude: identifier.coordinates.lat,
					longitude: identifier.coordinates.lng,
				},
			}
		: { placeId: identifier.placeId };

/**
 * Fetches location details for multiple locations in a single API call.
 * Returns suggestions in the same order as the input identifiers.
 */
async function fetchLocations(
	identifiers: LocationIdentifier[],
): Promise<(LocationSuggestion | null)[]> {
	if (identifiers.length === 0) {
		return [];
	}

	try {
		const response = await fetch
			.post<{ data: { suggestions: LocationSuggestion[] } }>(
				'cabs/api/v1/getLocation',
				{ json: { locations: identifiers.map(toRequestItem) } },
			)
			.json();

		const suggestions = response?.data?.suggestions ?? [];
		return identifiers.map((_, index) => suggestions[index] ?? null);
	} catch (error) {
		console.error('Failed to fetch locations:', error);
		return identifiers.map(() => null);
	}
}

/**
 * Fetches location details for both pickup and drop in a SINGLE API call.
 * Supports both coordinates and placeId for each location.
 */
export async function getLocationsFromIdentifiers(params: {
	pickup?: LocationIdentifier;
	drop?: LocationIdentifier;
}): Promise<{
	pickup: LocationSuggestion | null;
	drop: LocationSuggestion | null;
}> {
	const identifiers: LocationIdentifier[] = [];
	const pickupIndex = params.pickup ? identifiers.push(params.pickup) - 1 : -1;
	const dropIndex = params.drop ? identifiers.push(params.drop) - 1 : -1;

	const results = await fetchLocations(identifiers);

	return {
		pickup: pickupIndex >= 0 ? (results[pickupIndex] ?? null) : null,
		drop: dropIndex >= 0 ? (results[dropIndex] ?? null) : null,
	};
}
